import React, { useCallback, useMemo, useState, useEffect, Component, useRef } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  MarkerType,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import './flow-dark-theme.css';
import { AnimatePresence } from 'framer-motion';

// Custom node components
import { 
  InputNode, 
  CapabilityNode, 
  OutputNode, 
  ConditionNode, 
  TriggerNode 
} from './nodes';

// New n8n-style components
import NodePalette, { nodeDefinitions } from './NodePalette';
import NodeConfigPanelV2 from './NodeConfigPanelV2';
import FlowToolbar from './FlowToolbar';
import NodeContextMenu from './NodeContextMenu';
import NodeTestModal from './NodeTestModal';

// Error boundary for React Flow
class FlowErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Flow Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full w-full flex items-center justify-center bg-[#0a0b0f] text-white">
          <div className="text-center p-6">
            <p className="text-amber-400 mb-2">Visual Flow Editor encountered an error</p>
            <p className="text-sm text-slate-400">Please try refreshing the page</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * CapabilityFlowCanvas - Visual flow editor for agent capabilities
 * 
 * Uses React Flow to create a node-based editor where users can:
 * - Define input sources (triggers)
 * - Chain capabilities together
 * - Configure conditional logic
 * - Define outputs
 */

// Register custom node types
// Note: Using 'inputSource' and 'outputResult' to avoid conflicts with React Flow's built-in 'input' and 'output' types
const nodeTypes = {
  inputSource: InputNode,
  capability: CapabilityNode,
  outputResult: OutputNode,
  condition: ConditionNode,
  trigger: TriggerNode
};

// Default edge options
const defaultEdgeOptions = {
  type: 'default',
  animated: false,
  style: { stroke: '#4f46e5', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#4f46e5',
    width: 14,
    height: 14
  }
};

// Memoization helper for configToFlow
const configToFlowCache = new Map();
const getConfigHash = (config) => {
  // Include flow in hash to detect v2 flow changes
  const flowHash = config.flow ? JSON.stringify({
    version: config.flow.version,
    nodeCount: config.flow.nodes?.length || 0,
    edgeCount: config.flow.edges?.length || 0
  }) : 'no_flow';
  
  return JSON.stringify({
    inputs: config.accepted_inputs || [],
    required: config.required_capabilities || [],
    optional: config.optional_capabilities || [],
    outputs: config.expected_outputs || [],
    bindings: config.capability_bindings || {},
    flow: flowHash
  });
};

/**
 * Convert v2 flow nodes to ReactFlow format
 * Sets `bound` property based on node's binding configuration
 */
const convertV2FlowToReactFlow = (flow, agentConfig) => {
  const nodes = [];
  const edges = [];
  
  // Build capability bindings lookup from config OR from flow nodes
  const bindings = agentConfig.capability_bindings || {};
  
  // Also build lookup from required_capabilities (template agents mark all caps as bound by default)
  const requiredCaps = new Set(agentConfig.required_capabilities || []);
  
  // Convert nodes
  (flow.nodes || []).forEach(node => {
    // Determine node type mapping for ReactFlow visual rendering
    let reactFlowType = 'default';
    const originalType = node.type || ''; // Preserve original registry type
    
    if (originalType.startsWith('input.')) {
      reactFlowType = 'inputSource';
    } else if (originalType === 'capability.invoke' || originalType.startsWith('ai.')) {
      reactFlowType = 'capability';
    } else if (originalType.startsWith('output.')) {
      reactFlowType = 'outputResult';
    } else if (originalType.startsWith('trigger.')) {
      reactFlowType = 'trigger';
    } else if (originalType.startsWith('logic.')) {
      reactFlowType = 'condition';
    } else if (originalType.startsWith('action.')) {
      reactFlowType = 'capability'; // Actions render similar to capabilities
    } else if (originalType.startsWith('transform.') || originalType.startsWith('data.')) {
      reactFlowType = 'capability';
    } else if (originalType.startsWith('agent.')) {
      reactFlowType = 'capability';
    } else if (originalType.startsWith('integration.')) {
      reactFlowType = 'capability';
    } else if (originalType.startsWith('error.')) {
      reactFlowType = 'capability';
    } else if (originalType.startsWith('app.')) {
      reactFlowType = 'capability'; // Installed App Store apps
    } else if (originalType.startsWith('connected.')) {
      reactFlowType = 'capability'; // Connected integrations from unified registry
    }
    
    // Extract capability info for capability nodes - check multiple places
    const capability = node.params?.capability || 
                       node.data?.capability || 
                       node.binding?.capability_id || 
                       '';
    const isRequired = node.params?.required !== false && node.data?.required !== false;
    
    // Determine the actual node type for registry lookup
    // For capability.invoke nodes, use the capability ID to find the matching registry type
    let resolvedNodeType = originalType;
    if (originalType === 'capability.invoke' && capability) {
      // Try to find a matching action/capability type in common patterns
      // The capability ID might be 'send_email' which maps to 'action.send_email'
      resolvedNodeType = capability; // Store capability ID - the config panel will resolve it
    }
    
    // Determine if node is bound (configured) - comprehensive check
    // For template agents, all capabilities in required_capabilities are considered bound
    const hasBinding = !!(
      node.binding?.capability_id || 
      node.binding?.provider_type ||
      node.data?.bound ||
      bindings[capability] ||
      (capability && requiredCaps.has(capability)) ||
      // If the agent is from a template, all capability nodes should be bound
      (agentConfig.source_type === 'template' && reactFlowType === 'capability') ||
      (agentConfig.installed_from_template && reactFlowType === 'capability')
    );
    
    nodes.push({
      id: node.id,
      type: reactFlowType,
      position: node.position || { x: 0, y: 0 },
      data: {
        label: node.label || node.data?.label || 'Node',
        // IMPORTANT: Use resolved node type for config panel registry lookup
        // For capability.invoke nodes, this will be the capability ID (e.g., 'send_email')
        nodeType: resolvedNodeType,
        category: node.data?.category || resolvedNodeType.split('.')[0],
        // For input nodes
        inputType: originalType.replace('input.', '') || node.data?.inputType,
        // For capability nodes
        capability: capability,
        required: isRequired,
        bound: hasBinding,
        binding: node.binding || null,
        // Preserve params for config panel
        params: node.params || {},
        config: node.params || {}, // Alias for backward compatibility
        exec: node.exec || { enabled: true },
        // For output nodes
        outputType: node.params?.output_type || node.data?.outputType || 'text',
        // Pass through any other data
        ...node.data
      }
    });
  });
  
  // Convert edges
  (flow.edges || []).forEach(edge => {
    edges.push({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.source_handle || edge.sourceHandle,
      targetHandle: edge.target_handle || edge.targetHandle,
      ...defaultEdgeOptions
    });
  });
  
  return { nodes, edges };
};

// Convert agent config to flow nodes/edges (with caching)
const configToFlow = (agentConfig) => {
  const hash = getConfigHash(agentConfig);
  
  // Return cached result if config hasn't changed
  if (configToFlowCache.has(hash)) {
    return configToFlowCache.get(hash);
  }
  
  // Check if agent has a saved v2 flow - use that instead of regenerating
  if (agentConfig.flow && agentConfig.flow.nodes && agentConfig.flow.nodes.length > 0) {
    const result = convertV2FlowToReactFlow(agentConfig.flow, agentConfig);
    
    // Cache result
    if (configToFlowCache.size > 50) {
      const firstKey = configToFlowCache.keys().next().value;
      configToFlowCache.delete(firstKey);
    }
    configToFlowCache.set(hash, result);
    
    return result;
  }
  
  // No v2 flow - generate from legacy config arrays
  const nodes = [];
  const edges = [];
  
  // Layout constants
  const nodeSpacing = 120; // Vertical spacing between nodes
  const xInput = 100;
  const xCapability = 400;
  const xOutput = 700;

  // Get items for each column
  const inputs = agentConfig.accepted_inputs || [];
  const allCapabilities = [
    ...(agentConfig.required_capabilities || []),
    ...(agentConfig.optional_capabilities || [])
  ];
  
  // For truly empty configs, show empty canvas instead of default placeholder nodes
  // Only render nodes if user explicitly added them - not based on agent name alone
  const hasRealConfig = Boolean(
    allCapabilities.length > 0 ||
    (agentConfig.expected_outputs && agentConfig.expected_outputs.length > 0) ||
    (agentConfig.accepted_inputs && agentConfig.accepted_inputs.length > 0) ||
    (inputs.length > 0)
  );
  
  if (!hasRealConfig) {
    // Return empty canvas - no placeholder nodes
    return { nodes: [], edges: [] };
  }
  
  // DON'T add default capabilities - only render what user explicitly added
  // This prevents "ghost" nodes from appearing when user drops an input node
  const capsToRender = allCapabilities;
  
  // Only render outputs if user explicitly added them
  const outputs = agentConfig.expected_outputs?.length > 0 
    ? agentConfig.expected_outputs 
    : (agentConfig.output_schema?.types?.length > 0 ? agentConfig.output_schema.types : []);
  
  // If no capabilities and no outputs, just render inputs
  if (capsToRender.length === 0 && outputs.length === 0) {
    // Only show input nodes
    const inputStartY = 0;
    inputs.forEach((input, idx) => {
      const inputLabel = typeof input === 'string' ? input : input.label || 'Input';
      nodes.push({
        id: `input-${idx}`,
        type: 'inputSource',
        position: { x: xInput, y: inputStartY + idx * nodeSpacing },
        data: { 
          label: inputLabel,
          nodeType: `input.${inputLabel.toLowerCase().replace(/\s+/g, '_')}`,
          category: 'inputs'
        }
      });
    });
    return { nodes, edges };
  }
  
  // Calculate counts
  const inputCount = inputs.length;
  const capCount = capsToRender.length || 1; // At least 1 for layout
  const outputCount = outputs.length || 1; // At least 1 for layout
  
  // Find max column height for vertical alignment
  const maxItems = Math.max(inputCount, capCount, outputCount);
  
  // Calculate total height of the tallest column
  const totalHeight = (maxItems - 1) * nodeSpacing;
  
  // Helper function to calculate starting Y for centering a column
  // All columns should be vertically centered relative to each other
  const getColumnStartY = (itemCount) => {
    const columnHeight = (itemCount - 1) * nodeSpacing;
    return (totalHeight - columnHeight) / 2;
  };

  // Input nodes
  const inputStartY = getColumnStartY(inputCount);
  inputs.forEach((input, idx) => {
    const inputLabel = formatInputLabel(input);
    const inputId = typeof input === 'string' ? input : input.id || 'text';
    nodes.push({
      id: `input-${idx}`,
      type: 'inputSource',
      position: { x: xInput, y: inputStartY + idx * nodeSpacing },
      data: { 
        label: inputLabel,
        nodeType: `input.${inputId.toLowerCase().replace(/\s+/g, '_')}`,
        category: 'inputs',
        inputType: input,
        required: (agentConfig.required_inputs || []).includes(input),
        params: {},
        config: {},
        exec: { enabled: true }
      }
    });
  });

  // Capability nodes - only if there are capabilities defined
  if (capsToRender.length > 0) {
    const capStartY = getColumnStartY(capCount);
    
    // For template agents, all capabilities should be marked as bound
    const isTemplateAgent = agentConfig.source_type === 'template' || agentConfig.installed_from_template;
    
    capsToRender.forEach((cap, idx) => {
      const isRequired = (agentConfig.required_capabilities || []).includes(cap);
      // Mark as bound if: has explicit binding OR is a required capability of a template agent
      const isBound = !!(agentConfig.capability_bindings || {})[cap] || 
                      (isTemplateAgent && isRequired);
      
      // Determine category from capability ID (e.g., 'action.send_email' -> 'action')
      const category = cap.includes('.') ? cap.split('.')[0] : 'capabilities';
      
      nodes.push({
        id: `cap-${idx}`,
        type: 'capability',
        position: { x: xCapability, y: capStartY + idx * nodeSpacing },
        data: {
          label: formatCapabilityLabel(cap),
          // IMPORTANT: Store the full capability ID as nodeType for registry lookup
          nodeType: cap,
          category: category,
          capability: cap,
          required: isRequired,
          bound: isBound,
          params: {},
          config: {},
          exec: { enabled: true }
        }
      });

      // Connect inputs to first capability
      if (idx === 0) {
        inputs.forEach((_, inputIdx) => {
          edges.push({
            id: `e-input${inputIdx}-cap${idx}`,
            source: `input-${inputIdx}`,
            target: `cap-${idx}`,
            ...defaultEdgeOptions
          });
        });
      } else {
        // Chain capabilities
        edges.push({
          id: `e-cap${idx-1}-cap${idx}`,
          source: `cap-${idx - 1}`,
          target: `cap-${idx}`,
          ...defaultEdgeOptions
        });
      }
    });
  }

  // Output nodes - only if there are outputs defined
  if (outputs.length > 0) {
    const outputStartY = getColumnStartY(outputCount);
    outputs.forEach((output, idx) => {
      const outputLabel = typeof output === 'string' ? output : output.label || output;
      const outputId = typeof output === 'string' ? output : output.id || 'display';
      nodes.push({
        id: `output-${idx}`,
        type: 'outputResult',
        position: { x: xOutput, y: outputStartY + idx * nodeSpacing },
        data: {
          label: outputLabel,
          nodeType: `output.${outputId.toLowerCase().replace(/\s+/g, '_')}`,
          category: 'outputs',
          outputType: typeof output === 'string' ? 'text' : output.type || 'text',
          params: {},
          config: {},
          exec: { enabled: true }
        }
      });

      // Connect last capability to outputs
      if (capsToRender.length > 0) {
        edges.push({
          id: `e-cap${capsToRender.length - 1}-output${idx}`,
          source: `cap-${capsToRender.length - 1}`,
          target: `output-${idx}`,
          ...defaultEdgeOptions
        });
      } else if (inputs.length > 0) {
        // No capabilities - connect first input directly to output
        edges.push({
          id: `e-input0-output${idx}`,
          source: 'input-0',
          target: `output-${idx}`,
          ...defaultEdgeOptions
        });
      }
    });
  }

  const result = { nodes, edges };
  
  // Cache result and limit cache size
  if (configToFlowCache.size > 50) {
    const firstKey = configToFlowCache.keys().next().value;
    configToFlowCache.delete(firstKey);
  }
  configToFlowCache.set(hash, result);
  
  return result;
};

// Helper formatters
const formatInputLabel = (input) => {
  const labels = {
    text: 'Text Input',
    audio: 'Audio Input',
    file_pdf: 'PDF File',
    file_docx: 'Word Document',
    file_csv: 'CSV File',
    file_xlsx: 'Excel File',
    webhook: 'Webhook Trigger',
    api_payload: 'API Payload',
    url: 'URL',
    image: 'Image',
    email: 'Email Inbox',
    phone_call: 'Phone Call',
    schedule: 'Schedule Trigger',
    file_upload: 'File Upload',
    form: 'Form Input',
  };
  return labels[input] || input;
};

const formatCapabilityLabel = (cap) => {
  // Handle dot notation like 'process.classify' → 'Classify'
  const name = cap.includes('.') ? cap.split('.').pop() : cap;
  return name.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Inner component that uses React Flow hooks
const FlowCanvas = ({ 
  agentConfig, 
  onConfigUpdate,
  readOnly = false,
  // Execution animation props
  executionState = null,  // { phase: 'idle' | 'input' | 'processing' | 'output' | 'complete', currentNodeIndex: 0 }
  onExecutionComplete = null,
  // For node testing
  agentId = null,
  orgId = null,
  // Command modification highlighting
  highlightNodeIds = null,
}) => {
  // Convert config to nodes/edges
  const initialFlow = useMemo(() => configToFlow(agentConfig), [agentConfig]);
  
  const [nodes, setNodes, onNodesChange] = useNodesState(initialFlow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialFlow.edges);
  const [syncIndicator, setSyncIndicator] = useState(null);
  
  // n8n-style state
  const [showNodePalette, setShowNodePalette] = useState(!readOnly);
  const [selectedNode, setSelectedNode] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [history, setHistory] = useState({ past: [], future: [] });
  const [showShortcuts, setShowShortcuts] = useState(false);
  const reactFlowWrapper = useRef(null);
  
  // Context menu and test modal state
  const [contextMenu, setContextMenu] = useState({ isOpen: false, position: { x: 0, y: 0 }, node: null });
  const [testModal, setTestModal] = useState({ isOpen: false, node: null });
  
  // Get React Flow instance for fitView
  const reactFlowInstance = useReactFlow();

  // Apply execution animation to nodes
  useEffect(() => {
    if (!executionState || executionState.phase === 'idle') {
      // Reset all nodes to normal state
      setNodes(prevNodes => prevNodes.map(node => ({
        ...node,
        data: { ...node.data, executionStatus: null }
      })));
      return;
    }

    // Get node order: input nodes, capability nodes, output nodes
    const inputNodes = nodes.filter(n => n.type === 'inputSource');
    const capabilityNodes = nodes.filter(n => n.type === 'capability');
    const outputNodes = nodes.filter(n => n.type === 'outputResult');
    const orderedNodes = [...inputNodes, ...capabilityNodes, ...outputNodes];

    // Update nodes based on execution phase
    setNodes(prevNodes => prevNodes.map(node => {
      const nodeIndex = orderedNodes.findIndex(n => n.id === node.id);
      let status = null;

      if (nodeIndex === -1) return node;

      if (executionState.phase === 'complete') {
        status = 'success';
      } else if (nodeIndex < executionState.currentNodeIndex) {
        status = 'success';
      } else if (nodeIndex === executionState.currentNodeIndex) {
        status = 'processing';
      } else {
        status = 'pending';
      }

      return {
        ...node,
        data: { ...node.data, executionStatus: status }
      };
    }));

    // Update edges to show flow animation
    setEdges(prevEdges => prevEdges.map((edge, i) => ({
      ...edge,
      animated: executionState.phase !== 'idle' && executionState.phase !== 'complete',
      style: {
        ...edge.style,
        stroke: i < executionState.currentNodeIndex ? '#10B981' : '#6366F1',
        strokeWidth: i === executionState.currentNodeIndex - 1 ? 2.5 : 2
      }
    })));

  }, [executionState, setNodes, setEdges]);

  // Apply Command modification highlight to affected nodes
  useEffect(() => {
    if (!highlightNodeIds || highlightNodeIds.length === 0) return;
    setNodes(prevNodes => prevNodes.map(node => ({
      ...node,
      className: highlightNodeIds.includes(node.id)
        ? `${node.className || ''} command-highlight-node`.trim()
        : (node.className || '').replace('command-highlight-node', '').trim() || undefined,
    })));
    // Auto-clear highlight after 6 seconds
    const timer = setTimeout(() => {
      setNodes(prevNodes => prevNodes.map(node => ({
        ...node,
        className: (node.className || '').replace('command-highlight-node', '').trim() || undefined,
      })));
    }, 6000);
    return () => clearTimeout(timer);
  }, [highlightNodeIds, setNodes]);

  // Sync nodes/edges when config changes externally
  useEffect(() => {
    // Clear the cache to force re-computation with fresh config
    configToFlowCache.clear();
    const flow = configToFlow(agentConfig);
    setNodes(flow.nodes);
    setEdges(flow.edges);
  }, [agentConfig, setNodes, setEdges]);

  // Auto-fit view when nodes change significantly (e.g., when loading a different agent)
  useEffect(() => {
    if (nodes.length > 0 && reactFlowInstance) {
      // Debounce to avoid fitting on every minor change
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({ 
          padding: 0.3,
          maxZoom: 1,
          minZoom: 0.3,
          duration: 300,
          includeHiddenNodes: false
        });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [nodes.length, reactFlowInstance]);

  // Handle new connections
  const onConnect = useCallback((params) => {
    if (readOnly) return;
    setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds));
  }, [readOnly, setEdges]);

  // Handle node deletion - sync back to config
  const onNodesDelete = useCallback((deletedNodes) => {
    if (readOnly || !onConfigUpdate) return;

    const updates = {};
    
    deletedNodes.forEach(node => {
      if (node.type === 'inputSource') {
        updates.accepted_inputs = (agentConfig.accepted_inputs || [])
          .filter(i => i !== node.data.inputType);
      } else if (node.type === 'capability') {
        updates.required_capabilities = (agentConfig.required_capabilities || [])
          .filter(c => c !== node.data.capability);
        updates.optional_capabilities = (agentConfig.optional_capabilities || [])
          .filter(c => c !== node.data.capability);
      } else if (node.type === 'outputResult') {
        updates.expected_outputs = (agentConfig.expected_outputs || [])
          .filter(o => {
            const label = typeof o === 'string' ? o : o.label;
            return label !== node.data.label;
          });
      }
    });

    if (Object.keys(updates).length > 0) {
      onConfigUpdate(updates);
      setSyncIndicator('synced');
      setTimeout(() => setSyncIndicator(null), 1500);
    }
    setSelectedNode(null);
  }, [readOnly, onConfigUpdate, agentConfig]);

  // Handle node selection - auto-collapse palette when config panel opens
  const onNodeClick = useCallback((event, node) => {
    if (!readOnly) {
      setSelectedNode(node);
      setShowNodePalette(false); // Auto-collapse palette when editing a node
    }
  }, [readOnly]);

  // Handle right-click on node - show context menu
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      isOpen: true,
      position: { x: event.clientX, y: event.clientY },
      node
    });
  }, []);

  // Handle node test from context menu
  const handleNodeTest = useCallback((node) => {
    setTestModal({ isOpen: true, node });
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 }, node: null });
  }, []);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    closeContextMenu();
  }, [closeContextMenu]);

  // Handle drag over for node palette
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  // Handle drop from node palette
  const onDrop = useCallback((event) => {
    event.preventDefault();

    if (readOnly || !onConfigUpdate) return;

    try {
      const data = JSON.parse(event.dataTransfer.getData('application/json'));
      
      // Map palette node types to config updates
      if (data.category === 'inputs') {
        const inputMap = {
          'text_input': 'text',
          'file_input': 'file_pdf',
          'image_input': 'image',
          'audio_input': 'audio',
          'webhook_input': 'webhook'
        };
        const inputType = inputMap[data.nodeType] || data.nodeType;
        const current = agentConfig.accepted_inputs || [];
        if (!current.includes(inputType)) {
          onConfigUpdate({ accepted_inputs: [...current, inputType] });
        }
      } else if (data.category === 'outputs') {
        const outputMap = {
          'text_output': 'text_response',
          'json_output': 'json_data',
          'file_output': 'file',
          'webhook_output': 'webhook_response',
          'notification': 'notification'
        };
        const outputType = outputMap[data.nodeType] || data.nodeType;
        const current = agentConfig.expected_outputs || [];
        if (!current.includes(outputType)) {
          onConfigUpdate({ expected_outputs: [...current, outputType] });
        }
      } else if (data.category === 'triggers') {
        // Triggers go to accepted_inputs
        const current = agentConfig.accepted_inputs || [];
        if (!current.includes(data.nodeType)) {
          onConfigUpdate({ accepted_inputs: [...current, data.nodeType] });
        }
      } else {
        // All other categories (ai, agents, capabilities, logic, transform, actions, error_handling, integrations)
        // AND unified categories (installed_apps, connected_integrations)
        // go to required_capabilities
        const current = agentConfig.required_capabilities || [];
        if (!current.includes(data.nodeType)) {
          onConfigUpdate({ required_capabilities: [...current, data.nodeType] });
        }
      }

      setSyncIndicator('synced');
      setTimeout(() => setSyncIndicator(null), 1500);
    } catch (e) {
      console.error('Drop error:', e);
    }
  }, [readOnly, onConfigUpdate, agentConfig]);

  // Add node from palette click
  const handleNodeAdd = useCallback((node, category) => {
    if (readOnly || !onConfigUpdate) return;

    if (category === 'inputs') {
      const inputMap = {
        'text_input': 'text',
        'file_input': 'file_pdf',
        'image_input': 'image',
        'audio_input': 'audio',
        'webhook_input': 'webhook'
      };
      const inputType = inputMap[node.id] || node.id;
      const current = agentConfig.accepted_inputs || [];
      if (!current.includes(inputType)) {
        onConfigUpdate({ accepted_inputs: [...current, inputType] });
      }
    } else if (category === 'outputs') {
      const outputMap = {
        'text_output': 'text_response',
        'json_output': 'json_data',
        'file_output': 'file',
        'webhook_output': 'webhook_response',
        'notification': 'notification'
      };
      const outputType = outputMap[node.id] || node.id;
      const current = agentConfig.expected_outputs || [];
      if (!current.includes(outputType)) {
        onConfigUpdate({ expected_outputs: [...current, outputType] });
      }
    } else if (category === 'triggers') {
      const current = agentConfig.accepted_inputs || [];
      if (!current.includes(node.id)) {
        onConfigUpdate({ accepted_inputs: [...current, node.id] });
      }
    } else {
      // All other categories go to required_capabilities
      // Including installed_apps and connected_integrations from unified registry
      const current = agentConfig.required_capabilities || [];
      if (!current.includes(node.id)) {
        onConfigUpdate({ required_capabilities: [...current, node.id] });
      }
    }

    setSyncIndicator('synced');
    setTimeout(() => setSyncIndicator(null), 1500);
  }, [readOnly, onConfigUpdate, agentConfig]);

  // Update node config
  const handleNodeUpdate = useCallback((nodeId, updates) => {
    setNodes(nds => nds.map(n => 
      n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
    ));
  }, [setNodes]);

  // Delete selected node
  const handleDeleteSelected = useCallback(() => {
    if (selectedNode) {
      onNodesDelete([selectedNode]);
    }
  }, [selectedNode, onNodesDelete]);

  // Copy node
  const handleCopy = useCallback(() => {
    if (selectedNode) {
      setClipboard(selectedNode);
    }
  }, [selectedNode]);

  // Duplicate node
  const handleDuplicate = useCallback((node) => {
    if (!node) return;
    
    // Generate new unique ID
    const newId = `${node.type}-${Date.now()}`;
    
    // Create duplicated node with offset position
    const newNode = {
      ...node,
      id: newId,
      position: {
        x: (node.position?.x || 0) + 50,
        y: (node.position?.y || 0) + 50
      },
      data: {
        ...node.data,
        label: `${node.data?.label || 'Node'} (Copy)`
      },
      selected: false
    };
    
    // Add the new node to the flow
    setNodes((nds) => [...nds, newNode]);
    
    // Select the new node for editing
    setTimeout(() => {
      setSelectedNode(newNode);
    }, 100);
    
  }, [setNodes]);

  // Auto-layout function - resets nodes to calculated positions
  const handleAutoLayout = useCallback(() => {
    const flow = configToFlow(agentConfig);
    setNodes(flow.nodes);
    setEdges(flow.edges);
    
    // Fit view after layout
    setTimeout(() => {
      reactFlowInstance?.fitView({ 
        padding: 0.3, 
        maxZoom: 1, 
        duration: 400 
      });
    }, 50);
  }, [agentConfig, setNodes, setEdges, reactFlowInstance]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Show shortcuts modal on '?' key
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShowShortcuts(prev => !prev);
        return;
      }
      
      // Close shortcuts modal on Escape
      if (e.key === 'Escape' && showShortcuts) {
        setShowShortcuts(false);
        return;
      }
      
      if (readOnly) return;
      
      // Delete
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNode) {
        e.preventDefault();
        handleDeleteSelected();
      }
      // Copy
      if (e.ctrlKey && e.key === 'c' && selectedNode) {
        handleCopy();
      }
      // Fit view
      if (e.key === 'f' || e.key === 'F') {
        if (!e.ctrlKey && !e.metaKey) { // Don't interfere with Ctrl+F browser find
          e.preventDefault();
          reactFlowInstance?.fitView({ padding: 0.3, maxZoom: 1, duration: 300 });
        }
      }
      // Auto layout
      if (e.key === 'l' || e.key === 'L') {
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          handleAutoLayout();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [readOnly, selectedNode, handleDeleteSelected, handleCopy, showShortcuts, reactFlowInstance, handleAutoLayout]);

  return (
    <div className="flex h-full w-full" ref={reactFlowWrapper}>
      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setShowShortcuts(false)}
        >
          <div 
            className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-[12px] p-6 max-w-md w-full mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-white">Keyboard Shortcuts</h3>
              <button 
                onClick={() => setShowShortcuts(false)}
                className="text-[var(--text-muted)] hover:text-white p-1 rounded hover:bg-[var(--surface-3)] transition-colors"
              >
                <span className="text-sm">&#10005;</span>
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Fit to View</span>
                <kbd className="px-2 py-0.5 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[var(--text-muted)] text-xs">F</kbd>
              </div>
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Auto Layout</span>
                <kbd className="px-2 py-0.5 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[var(--text-muted)] text-xs">L</kbd>
              </div>
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Delete Selected</span>
                <kbd className="px-2 py-0.5 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[var(--text-muted)] text-xs">Delete</kbd>
              </div>
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Copy Node</span>
                <kbd className="px-2 py-0.5 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[var(--text-muted)] text-xs">Ctrl+C</kbd>
              </div>
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Zoom In</span>
                <kbd className="px-2 py-0.5 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[var(--text-muted)] text-xs">Ctrl+Scroll</kbd>
              </div>
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Pan Canvas</span>
                <kbd className="px-2 py-0.5 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[var(--text-muted)] text-xs">Click+Drag</kbd>
              </div>
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Show Shortcuts</span>
                <kbd className="px-2 py-0.5 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[var(--text-muted)] text-xs">?</kbd>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[var(--border-default)] text-xs text-[var(--text-muted)] text-center">
              Press <kbd className="px-1 bg-[var(--surface-4)] border border-[var(--border-default)] rounded text-[10px]">Esc</kbd> or click outside to close
            </div>
          </div>
        </div>
      )}
      
      {/* Node Palette (n8n-style sidebar) */}
      {!readOnly && (
        <NodePalette
          onNodeDragStart={() => {}}
          onNodeAdd={handleNodeAdd}
          isCollapsed={!showNodePalette}
          onToggle={() => {
            const willExpand = !showNodePalette;
            if (willExpand && selectedNode) {
              // Close config panel when expanding palette (mutually exclusive)
              setSelectedNode(null);
            }
            setShowNodePalette(willExpand);
          }}
          agentId={agentId}
        />
      )}

      {/* Main Canvas */}
      <div className="flex-1 relative">
        {/* Toolbar */}
        {!readOnly && (
          <FlowToolbar
            onUndo={() => {}}
            onRedo={() => {}}
            onDelete={handleDeleteSelected}
            onCopy={handleCopy}
            onPaste={() => {}}
            onZoomIn={() => reactFlowInstance?.zoomIn()}
            onZoomOut={() => reactFlowInstance?.zoomOut()}
            onFitView={() => reactFlowInstance?.fitView({ padding: 0.3, maxZoom: 1, duration: 300 })}
            onAutoLayout={handleAutoLayout}
            onShowShortcuts={() => setShowShortcuts(true)}
            canUndo={history.past.length > 0}
            canRedo={history.future.length > 0}
            hasSelection={!!selectedNode}
            hasClipboard={!!clipboard}
            readOnly={readOnly}
          />
        )}

        {/* Sync Indicator */}
        {syncIndicator && (
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-1.5 
            rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Config synced
          </div>
        )}
        
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={readOnly ? undefined : onNodesChange}
          onEdgesChange={readOnly ? undefined : onEdgesChange}
          onNodesDelete={readOnly ? undefined : onNodesDelete}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={onPaneClick}
          onDragOver={onDragOver}
          onDrop={onDrop}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          minZoom={0.2}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          className="react-flow-dark"
          deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
          selectionKeyCode={readOnly ? null : 'Shift'}
          multiSelectionKeyCode={readOnly ? null : 'Meta'}
          snapToGrid={true}
          snapGrid={[15, 15]}
          fitView
          fitViewOptions={{ padding: 0.3, minZoom: 0.3, maxZoom: 1 }}
        >
          <Controls 
            className="react-flow-controls-dark"
            showInteractive={!readOnly}
          />
          <MiniMap 
            className="react-flow-minimap-dark"
            nodeColor={(node) => {
              switch (node.type) {
                case 'inputSource': return '#3B82F6';
                case 'capability': return '#8B5CF6';
                case 'outputResult': return '#10B981';
                case 'condition': return '#F59E0B';
                case 'trigger': return '#EC4899';
                default: return '#6B7280';
              }
            }}
            maskColor="rgba(0, 0, 0, 0.8)"
          />
        </ReactFlow>
      </div>

      {/* Node Config Panel V2 - Registry-driven with Setup/Advanced tabs */}
      <AnimatePresence>
        {selectedNode && !readOnly && (
          <NodeConfigPanelV2
            node={selectedNode}
            onClose={() => {
              setSelectedNode(null);
              setShowNodePalette(true); // Restore palette when closing config panel
            }}
            onUpdate={handleNodeUpdate}
            onDelete={(id) => {
              onNodesDelete([selectedNode]);
              setShowNodePalette(true); // Restore palette after delete
            }}
            onDuplicate={handleDuplicate}
            onTest={handleNodeTest}
            availableProviders={[]}
            mcpTools={[]}
          />
        )}
      </AnimatePresence>

      {/* Node Context Menu */}
      <NodeContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        node={contextMenu.node}
        onClose={closeContextMenu}
        onTest={handleNodeTest}
        onConfigure={(node) => {
          setSelectedNode(node);
          setShowNodePalette(false);
        }}
        onDuplicate={handleDuplicate}
        onDelete={(node) => onNodesDelete([node])}
        readOnly={readOnly}
      />

      {/* Node Test Modal */}
      <NodeTestModal
        isOpen={testModal.isOpen}
        onClose={() => setTestModal({ isOpen: false, node: null })}
        node={testModal.node}
        agentId={agentId}
        flow={{ nodes, edges }}
        orgId={orgId}
      />
    </div>
  );
};

// Main component wrapped with ReactFlowProvider and error boundary
const CapabilityFlowCanvas = (props) => {
  return (
    <div className="h-full w-full bg-[#0a0b0f]">
      <FlowErrorBoundary>
        <ReactFlowProvider>
          <FlowCanvas {...props} />
        </ReactFlowProvider>
      </FlowErrorBoundary>
    </div>
  );
};

export default CapabilityFlowCanvas;
